/**
 * Normalize coach LLM markdown into a chat-friendly shape before rendering.
 * Presentation only — no move-grade tags or verdict chips.
 */

const FEN_RE =
  /\b([rnbqkpRNBQKP1-8]+\/){7}[rnbqkpRNBQKP1-8]+\s+[wb]\s+(?:[KQkq]+|-)\s+(?:[a-h][1-8]|-)\s+\d+\s+\d+\b/;

function looksLikeFen(s) {
  return FEN_RE.test(String(s || '').trim());
}

/** Convert pipe tables to bullet rows so they fit a narrow chat bubble. */
function tablesToBullets(md) {
  const lines = String(md || '').split('\n');
  const out = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    const next = lines[i + 1] || '';
    const isHeader = /^\s*\|.+\|\s*$/.test(line);
    const isSep = /^\s*\|?\s*:?-{3,}.*/.test(next) && next.includes('|');
    if (isHeader && isSep) {
      const headers = splitRow(line);
      i += 2; // skip header + separator
      while (i < lines.length && /^\s*\|.+\|\s*$/.test(lines[i])) {
        const cells = splitRow(lines[i]);
        const parts = headers.map((h, idx) => {
          const v = cells[idx] || '';
          if (!h && !v) return '';
          if (!h) return v;
          if (!v) return `**${h}**`;
          return `**${h}:** ${v}`;
        }).filter(Boolean);
        if (parts.length) out.push(`- ${parts.join(' — ')}`);
        i += 1;
      }
      out.push('');
      continue;
    }
    out.push(line);
    i += 1;
  }
  return out.join('\n');
}

function splitRow(line) {
  return String(line)
    .trim()
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split('|')
    .map((c) => c.trim());
}

/** Replace FEN-only fenced blocks / bare FEN lines with a short note. */
function softenFen(md) {
  let text = String(md || '');
  text = text.replace(/```(?:fen|plaintext|text)?\s*\n?([\s\S]*?)```/gi, (_, inner) => {
    const trimmed = String(inner || '').trim();
    if (looksLikeFen(trimmed)) {
      return '\n> Position is on the board — ask if you want the FEN.\n';
    }
    return `\`\`\`\n${trimmed}\n\`\`\``;
  });
  text = text.replace(FEN_RE, () => '_Position on the board_');
  return text;
}

/** Drop legacy VERDICT: lines if an older model reply still emits them. */
function stripLegacyVerdictLine(md) {
  return String(md || '').replace(/^\s*(?:\*\*)?VERDICT:\s*[^*\n]+(?:\*\*)?\s*\n+/i, '');
}

function demoteHeadings(md) {
  return String(md || '')
    .replace(/^#{1,6}\s+(.+)$/gm, '**$1**')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/** @returns {string} cleaned markdown */
export function beautifyCoachReply(raw) {
  let body = String(raw || '')
    .replace(/\u202f/g, ' ')
    .replace(/\u00a0/g, ' ')
    .trim();

  body = stripLegacyVerdictLine(body);
  body = softenFen(body);
  body = tablesToBullets(body);
  body = demoteHeadings(body);
  return body;
}

export default beautifyCoachReply;
